import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import './styles/app.css'
import { AppRoutes } from './app/router'
import { Providers } from './app/providers'
import { initTheme } from './app/theme'
import { initPreferences } from './app/preferences'
import { installErrorReporting } from './app/error-reporting'
import { sharedClock } from './lib/clock'

const root = document.getElementById('root')
if (!root) throw new Error('#root missing from index.html')

/*
  **First, before anything else can throw.**

  An error thrown while the theme is being read or while React is mounting is
  exactly the class of failure nobody sees, because it happens before whatever
  would have observed it exists. So the listeners go on ahead of `initTheme()`
  and ahead of the first render.

  The clock is the corrected one — `Clock.now()`, not `Date.now()` (invariant 7)
  — so two tabs reporting the same incident timestamp it against the same server
  rather than against two machines that disagree by a second. Before the first
  `pong` it is uncorrected, which is the right answer for an error report and
  would not be for a countdown.
*/
installErrorReporting({ now: () => sharedClock().now() })

// Before the first render, not inside it. `index.html` hard-codes
// `data-theme="dark"` so the first paint is never light-on-its-way-to-dark; this
// corrects it in the same task if the user chose otherwise, which is the
// difference between a theme preference and a visible flash of the wrong one.
initTheme()
// Layout preferences, on the same rule: read before the first render so the
// market page never mounts the standard panels and swaps them a task later.
// Order against `initTheme()` does not matter — both `mutate` `uiStore` rather
// than replacing it.
initPreferences()

createRoot(root).render(
  <StrictMode>
    <BrowserRouter>
      <Providers>
        <AppRoutes />
      </Providers>
    </BrowserRouter>
  </StrictMode>,
)
